import dayjs, { Dayjs } from 'dayjs';
import {
  BundleJson,
  BundlesPageJson,
  BundleStatus,
  Instruction,
  InstructionType,
} from '../../../types/bundleTypes';
import { settings } from '../../../settings';
import { plainToClass } from 'class-transformer';
import Fetch from 'utils/fetch';
import { Region } from '../../../types/orderTypes';
import { MonitorBundlesWithOrders } from '../../monitor/MonitorLive/Monitor.types';

export function setBackgroundColorByStatus(
  etaTime: Dayjs,
  performedAt: string | Dayjs
): string {
  if (!etaTime || !performedAt) {
    return 'white';
  }

  const diff = dayjs(etaTime).diff(performedAt, 'minute');
  if (diff > -2 && diff < 10) {
    return '#0eb227';
  } else if (diff < -14) {
    return '#dd4912';
  } else if (diff < 1) {
    return '#e88f2a';
  } else if (diff > -1) {
    return '#ffe200';
  } else {
    return '#FF8AFE';
  }
}

export function setColorByStatus(
  etaTime: Dayjs,
  performedAt: string | Dayjs
): string {
  if (!etaTime || !performedAt) {
    return 'white';
  }
  const diff = dayjs(etaTime).diff(performedAt, 'minute');
  if ((diff > -2 && diff < 10) || diff < 1) {
    return 'white';
  } else if (diff > -1) {
    return 'black';
  } else {
    return 'white';
  }
}

export function formatKm(meters: number, defaultValue = 'N/A'): string {
  if (meters <= 0) {
    return defaultValue;
  } else {
    const km = (Math.floor(meters * 10) / 10000).toFixed(1);
    return `${km.endsWith('.0') ? km.slice(0, -2) : km} Km`;
  }
}

export async function fetchBundlesWithStatus(
  statusList: BundleStatus[]
): Promise<BundleJson[]> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/bundle?status=${statusList.join(',')}`,
  });
  if (!response.ok) {
    throw Error(response.statusText);
  }
  const bundlesPage = plainToClass(BundlesPageJson, await response.json());
  return bundlesPage.items;
}

export async function fetchBundle(bundleId: string): Promise<BundleJson> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/bundle/${bundleId}`,
  });
  if (!response.ok) {
    throw Error(response.statusText);
  }
  return plainToClass(BundleJson, await response.json());
}

export async function fetchSelectedMonitorBundlesWithOrders(
  bundleIds: string[]
): Promise<MonitorBundlesWithOrders> {
  const response = await Fetch.post({
    url: `${settings.newApiUrl}/bundle/monitor-selected-bundles-with-orders`,
    body: {
      listOfBundleIds: bundleIds,
    },
  });
  if (!response.ok) {
    throw Error(response.statusText);
  }
  const jsonResponse = await response.json();
  return {
    bundles: plainToClass<BundleJson, BundleJson[]>(
      BundleJson,
      jsonResponse.bundles
    ),
    orders: jsonResponse.orders,
  };
}

export async function fetchAllMonitorBundlesWithOrders(
  region: Region | null
): Promise<MonitorBundlesWithOrders> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/bundle/monitor-all-bundles-with-orders${
      region ? `?&region=${region}` : ''
    }`,
  });
  if (!response.ok) {
    throw Error(response.statusText);
  }
  const jsonResponse = await response.json();
  return {
    bundles: plainToClass<BundleJson, BundleJson[]>(
      BundleJson,
      jsonResponse.bundles
    ),
    orders: jsonResponse.orders,
  };
}

export async function fetchBundlesPage(
  statusList: BundleStatus[],
  region: Region | null,
  currentPage: number,
  courierIds?: string[],
  todayCompleted?: boolean
): Promise<BundlesPageJson> {
  const response = await Fetch.get({
    url: `${settings.newApiUrl}/bundle?status=${statusList.join(
      ','
    )}&page=${currentPage}${region ? `&region=${region}` : ''}${
      courierIds && courierIds.length
        ? `&courierIds=${courierIds.join(',')}`
        : ''
    }${todayCompleted ? `&todayCompleted=true` : ''}`,
  });
  if (!response.ok) {
    throw Error(response.statusText);
  }
  return plainToClass(BundlesPageJson, await response.json());
}

export function bundleOrderIds(bundle: BundleJson): string[] {
  return Array.from(new Set(bundle.instructions.map(instr => instr.orderId)));
}

export function findNextInstruction(
  bundle: BundleJson,
  type: InstructionType
): Instruction | null {
  const instructionsWithType = bundle.instructions.filter(
    instr => instr.type === type
  );
  if (instructionsWithType.length > 0) {
    for (let i = instructionsWithType.length - 1; i >= 0; i--) {
      // find last one completed
      if (instructionsWithType[i].completed) {
        return instructionsWithType[i + 1];
      }
    }
    return instructionsWithType[0];
  }
  return null;
}

export function findLastCompletedInstruction(
  bundle: BundleJson,
  type: InstructionType
): Instruction {
  const deliveriesCompleted = bundle.instructions.filter(
    instr => instr.type === type && instr.completed
  );
  return (
    deliveriesCompleted.length > 0 &&
    deliveriesCompleted[deliveriesCompleted.length - 1]
  );
}

export async function cancelBundle(bundleId: string): Promise<boolean> {
  const response = await Fetch.put({
    url: `${settings.newApiUrl}/bundle/${bundleId}/cancel`,
  });
  return response.ok;
}
